import {Uniform3f} from '../../src/render/uniform_binding';
import {sRGBToLinearAndScale, linearVec3TosRGB, clamp} from '../../src/util/util';
import {lerp} from '../../src/style-spec/util/lerp';
import {vec3} from 'gl-matrix';

import type Context from '../../src/gl/context';
import type Style from '../../src/style/style';
import type Lights from '../style/lights';
import type {UniformValues} from '../../src/render/uniform_binding';
import type {LightProps as Ambient} from '../style/ambient_light_properties';
import type {LightProps as Directional} from '../style/directional_light_properties';

export type LightOverrides = {
    ambientIntensity?: number,
    ambientColor?: [number, number, number],
    directionalIntensity?: number,
    directionalColor?: [number, number, number]
};

export type LightsUniformsType = {
    ['u_lighting_ambient_color']: Uniform3f;
    ['u_lighting_directional_dir']: Uniform3f;
    ['u_lighting_directional_color']: Uniform3f;
    ['u_ground_radiance']: Uniform3f;
};

export const lightsUniforms = (context: Context): LightsUniformsType => ({
    'u_lighting_ambient_color': new Uniform3f(context),
    'u_lighting_directional_dir': new Uniform3f(context),
    'u_lighting_directional_color': new Uniform3f(context),
    'u_ground_radiance': new Uniform3f(context)
});

function calculateAmbientDirectionalFactor(dir: vec3, normal: vec3, dirColor: vec3): number {
    // NdotL Used only for ambient directionality
    const NdotL  = vec3.dot(normal, dir);

    // Emulate sky being brighter close to the main light source

    const factorReductionMax = 0.3;
    const dirLuminance = vec3.dot(dirColor, [0.2126, 0.7152, 0.0722]);
    const directionalFactorMin = 1.0 - factorReductionMax * Math.min(dirLuminance, 1.0);

    // If dirColor is (1, 1, 1), then the return value range is
    // NdotL=-1: 1.0 - factorReductionMax
    // NdotL>=0: 1.0
    const ambientDirectionalFactor = lerp(directionalFactorMin, 1.0, Math.min((NdotL + 1.0), 1.0));

    // Emulate environmental light being blocked by other objects

    // Value moves from vertical_factor_min at z=-1 to 1.0 at z=1
    const verticalFactorMin = 0.92;
    // clamp(z, -1.0, 1.0) is required because z can be very slightly out of the acceptable input
    // range for asin, even when it has been normalized, due to limited floating point precision.
    const verticalFactor = lerp(verticalFactorMin, 1.0, Math.asin(clamp(normal[2], -1.0, 1.0)) / Math.PI + 0.5);

    return verticalFactor * ambientDirectionalFactor;
}

function calculateGroundRadiance(dir: vec3, dirColor: [number, number, number], ambientColor: [number, number, number]): [number, number, number] {
    const groundNormal: [number, number, number] = [0.0, 0.0, 1.0];
    const ambientDirectionalFactor = calculateAmbientDirectionalFactor(dir, groundNormal, dirColor);

    const ambientContrib: [number, number, number] = [0, 0, 0];
    vec3.scale(ambientContrib, ambientColor.slice(0, 3), ambientDirectionalFactor);
    const dirContrib: [number, number, number] = [0, 0, 0];
    vec3.scale(dirContrib, dirColor.slice(0, 3), dir[2]);

    const radiance: [number, number, number] = [0, 0, 0];
    vec3.add(radiance, ambientContrib, dirContrib);

    return linearVec3TosRGB(radiance);
}

export const lightsUniformValues = (directional: Lights<Directional>, ambient: Lights<Ambient>, style: Style, lightOverrides?: LightOverrides): UniformValues<LightsUniformsType> => {

    const direction = directional.properties.get('direction');

    const dirIgnoreLut = directional.properties.get('color-use-theme') === 'none';
    const directionalColor = directional.properties.get('color').toNonPremultipliedRenderColor(dirIgnoreLut ? null : style.getLut(directional.scope)).toArray01();
    let directionalIntensity = directional.properties.get('intensity');

    const ambIgnoreLut = ambient.properties.get('color-use-theme') === 'none';
    const ambientColor = ambient.properties.get('color').toNonPremultipliedRenderColor(ambIgnoreLut ? null : style.getLut(ambient.scope)).toArray01();
    let ambientIntensity = ambient.properties.get('intensity');

    const dirVec: [number, number, number] = [direction.x, direction.y, direction.z];

    if (lightOverrides) {
        if (lightOverrides.ambientIntensity !== undefined) ambientIntensity = lightOverrides.ambientIntensity;
        if (lightOverrides.directionalIntensity !== undefined) directionalIntensity = lightOverrides.directionalIntensity;
        if (lightOverrides.ambientColor !== undefined) {
            ambientColor[0] = lightOverrides.ambientColor[0];
            ambientColor[1] = lightOverrides.ambientColor[1];
            ambientColor[2] = lightOverrides.ambientColor[2];
        }
        if (lightOverrides.directionalColor !== undefined) {
            directionalColor[0] = lightOverrides.directionalColor[0];
            directionalColor[1] = lightOverrides.directionalColor[1];
            directionalColor[2] = lightOverrides.directionalColor[2];
        }
    }

    const ambientColorLinear = sRGBToLinearAndScale(ambientColor, ambientIntensity);

    const directionalColorLinear = sRGBToLinearAndScale(directionalColor, directionalIntensity);
    const groundRadianceSrgb = calculateGroundRadiance(dirVec, directionalColorLinear, ambientColorLinear);
    return {
        'u_lighting_ambient_color': ambientColorLinear,
        'u_lighting_directional_dir': dirVec,
        'u_lighting_directional_color': directionalColorLinear,
        'u_ground_radiance': groundRadianceSrgb
    };
};
